/* eslint-disable max-depth -- отключаю для улучшения читаемости файла */
import { type MaybePromise, safeJsonStringify } from '@direct-frontend/stdlib';
import { UnexpectedError } from '@adv-frontend/error';
import type { Flow, AsyncFlow, SyncFlowGate, SyncFlowValue } from '@adv-frontend/modules-flow';
import type { DataSourceBrand, Unsubscribe, Scheduler, IterableDataSource, DataSource } from '../types';
import {
    currentReactionDataSources,
    setIsDataSourceCollecting,
    getIsDataSourceCollecting,
} from '../currentReactionDataSources';
import { createPromiseWithResolvers, reportError } from '../utils';
import { SideEffectInDataError } from './errors';
import { ReactionMonitoring } from './reactionMonitoring';
import { isPromise, isSkipToken, SKIP_EFFECT_TOKEN } from './helpers';
import { addRunningReactionToRegistry } from './globalRegistry';

/**
 * Интерфейс взаимодействия с реакцией изнутри дата-генератора
 */
export type ReactionDataApi<P> = {
    /**
     * Отключает подписку на изменения источников данных (см. пример с takeLeading)
     */
    stopDataSourcesSubscriptions: () => void;
    /**
     * Включает подписку на изменение источников данных (см. пример с takeLeading)
     */
    startDataSourcesSubscriptions: () => void;
    /**
     * Отменяет выполнение других запусков текущего экземпляра реакции (см. пример с takeLatest)
     */
    cancelActiveRuns: () => void;
    /**
     * Отменяет выполнение других запусков всех экземпляров реакций
     */
    cancelAnyParamsActiveRuns: () => void;
    /**
     * AbortSignal текущего запуска
     */
    signal: AbortSignal;
    /**
     * Метод позволяющий пропустить выполнение эффекта
     */
    skipEffect: () => typeof SKIP_EFFECT_TOKEN;
    /**
     * Параметр запуска экземпляра реакции
     */
    param: P;
    /**
     * Данные переданные в эффект из `data` в предыдущем запуске. Если это первый запуск, то undefined
     * @deprecated дата-генератор не должен ориентироваться на предыдущие запуски
     */
    prevData: unknown;
    /**
     * Подписывается на синхронный Flow и возвращает текущее значение потока.
     * Реакция будет перезапускаться при изменении значения в Flow.
     *
     * Асинхронный поток (`AsyncFlow`) сюда передать нельзя — гейт `SyncFlowGate`
     * из `@adv-frontend/modules-flow` дает ошибку типов. Синхронное чтение асинхронного
     * потока заставило бы дата-генератор вручную разбирать `pending`/`error`, и учесть все
     * корнер-кейсы тяжело. Для асинхронных источников есть {@link ReactionDataApi.watchAsync}.
     *
     * В обобщенном хелпере параметр объявляйте как `Flow<Data>`: гейт считается по типу
     * аргумента, и для еще не выведенного типа-параметра его вычислить нельзя.
     *
     * @typeParam TFlow - тип отслеживаемого Flow
     * @param flow - синхронный Flow для отслеживания
     * @returns Текущее значение Flow
     *
     * @example
     * ```ts
     * const counterFlow = createFlow(0);
     *
     * const reaction = createReaction({
     *     *data(_, reactionApi) {
     *         const count = reactionApi.watch(counterFlow);
     *         return count;
     *     },
     *     effect(count) {
     *         console.log('Current count:', count);
     *     }
     * });
     * ```
     */
    watch<TFlow extends Flow<unknown>>(flow: TFlow & SyncFlowGate<TFlow>): SyncFlowValue<TFlow>;

    /**
     * Утилиты для отслеживания и ожидания асинхронных источников потоков.
     */
    watchAsync: {
        /**
         * Подписывается на асинхронный AsyncFlow и возвращает iterable data source для получения значения.
         * Реакция будет перезапускаться при изменении значения в AsyncFlow.
         *
         * @typeParam T - тип данных в AsyncFlow
         * @param flow - асинхронный AsyncFlow для отслеживания
         * @returns Iterable data source, который при итерации ожидает промис и возвращает полученное значение
         *
         * @example
         * ```ts
         * const reaction = createReaction({
         *     *data(_, reactionApi) {
         *         const user = yield* reactionApi.watchAsync(endpoint.query());
         *         return user;
         *     },
         *     effect(user) {
         *         console.log('User:', user);
         *     }
         * });
         * ```
         */
        <T>(flow: AsyncFlow<T>): IterableDataSource<symbol, Promise<void>, T>;

        /**
         * Ожидает вычисления всех асинхронных потоков, аналогично Promise.all.
         *
         * @param flows - Массив асинхронных потоков для ожидания
         * @returns Promise generator, который разрешается в массив всех значений потоков
         * @throws Выбросит ошибку, если какой-либо из потоков завершится ошибкой
         */
        all<T extends readonly AsyncFlow<unknown>[] | []>(
            flows: T
        ): IterableDataSource<symbol, Promise<void>, { -readonly [K in keyof T]: AwaitedFlow<T[K]> }>;

        /**
         * Ожидает вычисления всех асинхронных потоков, аналогично Promise.allSettled.
         *
         * @param flows - Массив асинхронных потоков для ожидания
         * @returns Promise generator, который разрешается в массив завершенных результатов
         */
        allSettled<T extends readonly AsyncFlow<unknown>[] | []>(
            flows: T
        ): IterableDataSource<
            symbol,
            Promise<void>,
            { -readonly [K in keyof T]: PromiseSettledResult<AwaitedFlow<T[K]>> }
        >;

        /**
         * Ожидает вычисления любого асинхронного потока, аналогично Promise.any.
         *
         * @param flows - Массив асинхронных потоков
         * @returns Promise generator, который разрешается в первое успешное значение потока
         * @throws Выбросит ошибку, если все потоки отклонятся
         */
        any<T extends readonly AsyncFlow<unknown>[] | []>(
            flows: T
        ): IterableDataSource<symbol, Promise<void>, AwaitedFlow<T[number]>>;

        /**
         * Ожидает первого вычисления любого асинхронного потока, аналогично Promise.race.
         *
         * @param flows - Массив асинхронных потоков
         * @returns Promise generator, который разрешается в первое завершенное значение потока
         */
        race<T extends readonly AsyncFlow<unknown>[] | []>(
            flows: T
        ): IterableDataSource<symbol, Promise<void>, AwaitedFlow<T[number]>>;
    };
};

/**
 * Интерфейс взаимодействия с реакцией изнутри эффекта
 */
export type ReactionEffectApi<R, P> = {
    /**
     * Отключает подписку на изменения источников данных (см. пример с takeLeading)
     */
    stopDataSourcesSubscriptions: () => void;
    /**
     * Включает подписку на изменение источников данных (см. пример с takeLeading)
     */
    startDataSourcesSubscriptions: () => void;
    /**
     * Отменяет выполнение других запусков текущего экземпляра реакции (см. пример с takeLatest)
     */
    cancelActiveRuns: () => void;
    /**
     * Отменяет выполнение других запусков всех экземпляров реакций
     */
    cancelAnyParamsActiveRuns: () => void;
    /**
     * Данные переданные в эффект из `data` в предыдущем запуске. Если это первый запуск, то undefined
     */
    prevData: R | undefined;
    /**
     * Дождаться завершения всех запущенных до текущей реакций
     */
    waitPreviousReactions: () => Promise<void>;
    /**
     * AbortSignal текущего запуска
     */
    signal: AbortSignal;
    /**
     * Параметр запуска экземпляра реакции
     */
    param: P;
};

/**
 * Вспомогательный тип, который извлекает тип значения из AsyncFlow.
 */
type AwaitedFlow<T> = T extends AsyncFlow<infer U> ? U : never;

/**
 * Тип функции-генератора для получения данных реакции
 * @typeParam R - тип возвращаемых данных
 * @typeParam P - тип параметра запуска реакции
 */
export type DataGenerator<R, P> = (
    param: P,
    reactionApi: ReactionDataApi<P>
) => Generator<MaybePromise<unknown> & DataSourceBrand, R | typeof SKIP_EFFECT_TOKEN, undefined>;

/**
 * Тип функции эффекта, выполняемой после получения данных
 * @typeParam R - тип данных из генератора
 * @typeParam P - тип параметра запуска реакции
 */
export type EffectFn<R, P> = (result: R, reactionApi: ReactionEffectApi<R, P>) => MaybePromise<void>;

/**
 * Стейт для каждого запуска реакции
 */
class Execution {
    /**
     * AbortController текущего запуска экземпляра реакции
     */
    abortController = new AbortController();

    /**
     * Промис запуска (null если экземпляр создается но еще запущен)
     */
    promise: Promise<void> | null = null;

    /**
     * Было ли завершено выполнение дата-генератора
     */
    isDataLoading = true;
}

/**
 * Экземпляр реакции. У одной реакции может быть несколько экземпляров,
 * которые различаются между собой параметрами запуска реакции.
 */
export class ReactionInstance<R, P> {
    /**
     * Параметр запуска экземпляра реакции
     */
    public readonly param: P;

    /**
     * Функция-генератор для получения данных реакции
     */
    private readonly data: DataGenerator<R, P>;

    /**
     * Функция эффекта, выполняемая после получения данных
     */
    private readonly effect: EffectFn<R, P>;

    /**
     * Планировщик для выполнения реакции
     */
    private readonly scheduler: Scheduler;

    /**
     * Экземпляры без потребителей, у которых остались незавершенные запуски (для cancelAnyParamsActiveRuns)
     */
    private readonly orphanedInstances: Set<ReactionInstance<R, P>>;

    /**
     * Ошибка для трекинга (содержит stacktrace)
     */
    private readonly reactionError: Error;

    /**
     * Мониторинг реакции
     */
    private readonly monitoring: ReactionMonitoring;

    /**
     * Ссылка на все экземпляры (для cancelAnyParamsActiveRuns)
     */
    private readonly instances: Map<P, ReactionInstance<R, P>>;

    /**
     * Функции отписок от изменений источников
     */
    public readonly unsubscribes: Set<Unsubscribe> = new Set();

    /**
     * Количество использований экземпляра
     */
    private countOfUses: number = 1;

    /**
     * Флаг информирующий о наличии отложенного запуска реакции. Используется для батчинга
     */
    private hasDeferredRun: boolean = false;

    /**
     * Количество уведомлений об изменении источников данных. Очищается при каждом запуске
     */
    private notificationsCount: number = 0;

    /**
     * Данные переданные в эффект из `data` в предыдущем запуске. Если это первый запуск экземпляра, то undefined
     */
    private prevData: R | undefined;

    /**
     * Флаг приостановки подписок на изменения источников данных
     */
    private isSubscriptionsStopped: boolean = false;

    /**
     * Незавершенные запуски реакции
     */
    private executions: Array<Execution> = [];

    /**
     * Создает новый экземпляр реакции
     * @param options - параметры создания экземпляра реакции
     * @param options.param - параметр запуска экземпляра реакции
     * @param options.data - функция-генератор для получения данных
     * @param options.effect - функция эффекта
     * @param options.orphanedInstances - ссылка на отцепленные экземпляры с незавершенными запусками
     * @param options.scheduler - планировщик выполнения
     * @param options.reactionError - ошибка для трекинга (содержит stacktrace)
     * @param options.reactionMonitoring - мониторинг реакции
     * @param options.instances - ссылка на все экземпляры (для cancelAnyParamsActiveRuns)
     */
    public constructor(options: {
        param: P;
        data: DataGenerator<R, P>;
        effect: EffectFn<R, P>;
        orphanedInstances: Set<ReactionInstance<R, P>>;
        scheduler: Scheduler;
        reactionError: Error;
        reactionMonitoring: ReactionMonitoring;
        instances: Map<P, ReactionInstance<R, P>>;
    }) {
        this.param = options.param;
        this.data = options.data;
        this.effect = options.effect;
        this.orphanedInstances = options.orphanedInstances;
        this.scheduler = options.scheduler;
        this.reactionError = options.reactionError;
        this.monitoring = options.reactionMonitoring;
        this.instances = options.instances;
    }

    /**
     * Отписаться от используемых источников
     */
    private unsubscribeFromDataSources() {
        this.unsubscribes.forEach(unsubscribe => unsubscribe());
        this.unsubscribes.clear();
    }

    /**
     * Отключает подписку на изменения источников данных
     */
    private stopDataSourcesSubscriptions() {
        this.isSubscriptionsStopped = true;
    }

    /**
     * Включает подписку на изменение источников данных
     */
    private startDataSourcesSubscriptions() {
        this.isSubscriptionsStopped = false;
    }

    /**
     * Забирает все сложенные в currentReactionDataSource источники для подписки и подписывает экземпляр
     *
     * @param onNotify - колбэк для запуска на изменения данных в источниках
     */
    private subscribeToCurrentReactionDataSources(onNotify: () => void) {
        // Если экземпляр не используется, то и не создает подписки
        if (this.countOfUses !== 0) {
            currentReactionDataSources.forEach(dataSourceSubscribe => {
                this.unsubscribes.add(dataSourceSubscribe(onNotify));
            });
        }

        currentReactionDataSources.clear();
    }

    /**
     * Останавливает текущие и отложенные (из-за батчинга) запуски экземпляра реакции
     *
     * @param currentExecution - текущий запуск не будет остановлен (если передан)
     */
    public cancelActiveRuns(currentExecution?: Execution) {
        for (const execution of this.executions) {
            if (!currentExecution || execution !== currentExecution) {
                execution.abortController.abort();
            } else {
                break;
            }
        }
    }

    /**
     * Создает ReactionApi для data функций
     * @param currentExecution - текущий запуск реакции
     * @returns API для взаимодействия с реакцией изнутри
     */
    private createReactionDataApi(currentExecution: Execution): ReactionDataApi<P> {
        const watchAsync: ReactionDataApi<P>['watchAsync'] = flow => {
            return this.toIterator(this.watchAsyncFlow(flow, currentExecution));
        };

        watchAsync.all = <T extends readonly AsyncFlow<unknown>[] | []>(flows: T) => {
            const promise = Promise.all(flows.map(flow => this.watchAsyncFlow(flow, currentExecution)));
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- приводим типы в соответствие с Promise.all
            return this.toIterator(promise as Promise<{ -readonly [K in keyof T]: AwaitedFlow<T[K]> }>);
        };

        watchAsync.allSettled = <T extends readonly AsyncFlow<unknown>[] | []>(flows: T) => {
            const promise = Promise.allSettled(flows.map(flow => this.watchAsyncFlow(flow, currentExecution)));
            return this.toIterator(
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- приводим типы в соответствие с Promise.allSettled
                promise as Promise<{ -readonly [K in keyof T]: PromiseSettledResult<AwaitedFlow<T[K]>> }>
            );
        };

        watchAsync.any = <T extends readonly AsyncFlow<unknown>[] | []>(flows: T) => {
            const promise = Promise.any(flows.map(flow => this.watchAsyncFlow(flow, currentExecution)));
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- приводим типы в соответствие с Promise.any
            return this.toIterator(promise as Promise<AwaitedFlow<T[number]>>);
        };

        watchAsync.race = <T extends readonly AsyncFlow<unknown>[] | []>(flows: T) => {
            const promise = Promise.race(flows.map(flow => this.watchAsyncFlow(flow, currentExecution)));
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- приводим типы в соответствие с Promise.race
            return this.toIterator(promise as Promise<AwaitedFlow<T[number]>>);
        };

        return {
            startDataSourcesSubscriptions: () => this.startDataSourcesSubscriptions(),
            stopDataSourcesSubscriptions: () => this.stopDataSourcesSubscriptions(),
            signal: currentExecution.abortController.signal,
            param: this.param,
            prevData: this.prevData,
            skipEffect: () => SKIP_EFFECT_TOKEN,
            cancelActiveRuns: () => {
                this.cancelActiveRuns(currentExecution);
            },
            cancelAnyParamsActiveRuns: () => {
                for (const instance of this.instances.values()) {
                    instance.cancelActiveRuns(currentExecution);
                }
                this.cancelOrphanedRuns();
            },
            watch: <TFlow extends Flow<unknown>>(flow: TFlow & SyncFlowGate<TFlow>) =>
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- гейт разрывает связь TFlow с типом данных потока, восстанавливаем ее вручную
                this.watchFlow(flow) as SyncFlowValue<TFlow>,
            watchAsync,
        };
    }

    /**
     * Функция для отслеживания синхронного Flow.
     * При изменении значения в Flow, планирует уведомление реакции через scheduler.
     *
     * @typeParam T - тип данных в Flow
     * @param flow - синхронный Flow для отслеживания
     * @returns Текущее значение Flow на момент вызова
     */
    private watchFlow<T>(flow: Flow<T>): T {
        const subscribe = (initialSnapshot: unknown) => {
            let isActive = true;
            let prevSnapshot = initialSnapshot;

            currentReactionDataSources.add(notify => {
                const subscription = flow.subscribe(() => {
                    this.scheduler.schedule(() => {
                        // не обрабатываем нотификацию, если в процессе планирования успели отменить подписку
                        if (!isActive) {
                            return;
                        }

                        const snapshot = flow.getSnapshot();

                        // Запускаем реакции только если данные изменились
                        if (prevSnapshot !== snapshot) {
                            prevSnapshot = snapshot;
                            notify();
                        }
                    });
                });

                return () => {
                    isActive = false;
                    subscription.unsubscribe();
                };
            });
        };

        try {
            const snapshot = flow.getSnapshot();
            subscribe(snapshot);
            return snapshot;
        } catch (err) {
            subscribe({});
            throw err;
        }
    }

    /**
     * Функция для отслеживания асинхронного AsyncFlow.
     * Создает подписку на изменения AsyncFlow и возвращает iterable data source,
     * который при итерации ожидает разрешения промиса и возвращает полученное значение.
     *
     * @typeParam T - тип данных в AsyncFlow
     * @param flow - асинхронный AsyncFlow для отслеживания
     * @param execution - текущее выполнение реакции
     * @returns Iterable data source с символьным ключом, который yield'ит промис и возвращает значение типа T
     */
    private watchAsyncFlow<T>(flow: AsyncFlow<T>, execution: Execution): Promise<T> {
        const subscribe = (initialSnapshot: unknown) => {
            let prevSnapshot = initialSnapshot;
            let isActive = true;

            currentReactionDataSources.add(notify => {
                const subscription = flow.subscribe(() => {
                    // Не реагируем на первоначальную загрузку, так как реакция уже ждет соответствующий промис
                    if (execution.isDataLoading) {
                        return;
                    }

                    this.scheduler.schedule(() => {
                        // не обрабатываем нотификацию, если в процессе планирования успели отменить подписку
                        if (!isActive) {
                            return;
                        }

                        const snapshot = flow.getSnapshot();

                        // Игнорируем все изменения, кроме завершения запроса
                        if (snapshot.status === 'pending') {
                            return;
                        }

                        // Запускаем реакции только если данные изменились
                        if (prevSnapshot !== snapshot) {
                            prevSnapshot = snapshot;
                            notify();
                        }
                    });
                });

                return () => {
                    isActive = false;
                    subscription.unsubscribe();
                };
            });
        };

        try {
            const snapshot = flow.getSnapshot();
            subscribe(snapshot);
            return flow.asPromise();
        } catch (err) {
            subscribe({});
            throw err;
        }
    }

    /**
     * Преобразует Promise в итератор, который может использоваться с синтаксисом генераторов.
     *
     * @typeParam T - Тип значения, в которое разрешается промис
     * @param promise - Промис для преобразования
     * @returns Итератор, который может быть использован yield в функциях-генераторах
     */
    private toIterator<T>(promise: Promise<T>): IterableDataSource<symbol, Promise<void>, T> {
        return Object.assign(Symbol(), {
            [Symbol.iterator]: function* () {
                let valueRef: { current: T };

                const iteratorPromise = promise.then(data => {
                    valueRef = { current: data };
                });

                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- кастим к брендированному типу
                yield iteratorPromise as DataSource<Promise<void>>;

                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- value гарантировано существует, так как yield ждет разрешения промиса
                return valueRef!.current;
            },
        });
    }

    /**
     * Создает ReactionApi для effect функций
     * @param currentExecution - текущий запуск реакции
     * @returns API для взаимодействия с реакцией изнутри
     */
    private createReactionEffectApi(currentExecution: Execution): ReactionEffectApi<R, P> {
        return {
            prevData: this.prevData,
            startDataSourcesSubscriptions: () => this.startDataSourcesSubscriptions(),
            stopDataSourcesSubscriptions: () => this.stopDataSourcesSubscriptions(),
            signal: currentExecution.abortController.signal,
            param: this.param,
            cancelActiveRuns: () => {
                this.cancelActiveRuns(currentExecution);
            },
            cancelAnyParamsActiveRuns: () => {
                for (const instance of this.instances.values()) {
                    instance.cancelActiveRuns(currentExecution);
                }
                this.cancelOrphanedRuns();
            },
            waitPreviousReactions: async () => {
                const currentExecutionIndex = this.executions.findIndex(execution => execution === currentExecution);
                if (currentExecutionIndex === -1) {
                    throw new UnexpectedError(
                        'В waitPreviousReactions отсутствует текущий запуск в instance.executions',
                        {
                            cause: null,
                        }
                    );
                }
                await Promise.allSettled(
                    this.executions.slice(0, currentExecutionIndex).map(execution => execution.promise)
                );
            },
        };
    }

    /**
     * Увеличивает счетчик использований экземпляра при подключении
     */
    public onAttach(): void {
        this.countOfUses++;
    }

    /**
     * Уменьшает счетчик использований экземпляра при отключении
     */
    public onDetach(): void {
        this.countOfUses--;
    }

    /**
     * Проверяет, используется ли экземпляр
     * @returns true, если экземпляр имеет активных пользователей
     */
    public hasUsages(): boolean {
        return this.countOfUses !== 0;
    }

    /**
     * Уничтожает экземпляр реакции, останавливая подписки и отписываясь от источников данных.
     *
     * Незавершенные запуски не отменяет: запланированный запуск доводится до эффекта даже после
     * отписки последнего потребителя.
     */
    public destroy(): void {
        this.stopDataSourcesSubscriptions();
        this.unsubscribeFromDataSources();
    }

    /**
     * Есть ли у экземпляра незавершенные запуски
     *
     * @returns true, если хотя бы один запуск еще выполняется
     */
    public hasActiveRuns(): boolean {
        return this.executions.length > 0;
    }

    /**
     * Останавливает запуски экземпляров, у которых уже не осталось потребителей.
     *
     * Такой экземпляр удален из кеша реакции, поэтому обход `instances` его не находит, а его
     * запуск может дождаться асинхронного источника уже после того, как параметр сменился, и
     * перезаписать свежий результат устаревшим.
     */
    private cancelOrphanedRuns(): void {
        for (const instance of this.orphanedInstances) {
            instance.cancelActiveRuns();
        }

        this.orphanedInstances.clear();
    }

    /**
     * Возвращает последний незавершенный запуск реакции
     * @returns последний запуск или undefined, если нет активных запусков
     */
    public getLastExecution(): Execution | undefined {
        return this.executions[this.executions.length - 1];
    }

    /**
     * Возвращает набор промисов для всех активных запусков реакции.
     * @returns массив промисов
     */
    public getAllExecutionPromises(): Promise<void>[] {
        return this.executions.map(execution => execution.promise).filter(promise => promise !== null);
    }

    /**
     * Запланировать выполнение реакции с использованием выбранного планировщика
     *
     * @returns промис запуска. Промис резолвится при завершении выполнения *data+effect
     */
    public schedule(): void {
        const { promise, resolve, reject } = createPromiseWithResolvers();

        const execution = new Execution();
        this.executions.push(execution);
        this.hasDeferredRun = true;

        this.monitoring.onSchedule(this.executions.length);
        this.scheduler.schedule(() => this.execute(execution).then(resolve, reject));

        execution.promise = addRunningReactionToRegistry(promise);
    }

    /**
     * Главный метод выполнения реакции - запускает data генератор и effect
     * @param currentExecution - текущий запуск реакции
     */
    private async execute(currentExecution: Execution): Promise<void> {
        this.monitoring.onExecute();
        this.notificationsCount = 0;
        this.hasDeferredRun = false;

        try {
            const reactionDataApi = this.createReactionDataApi(currentExecution);
            const reactionEffectApi = this.createReactionEffectApi(currentExecution);

            /**
             * Отписываемся от текущих подписок, т.к. они могут быть не актуальными из-за условного кода
             */
            this.unsubscribeFromDataSources();

            let iteratorResult: IteratorResult<DataSourceBrand, R | typeof SKIP_EFFECT_TOKEN> | undefined = undefined;
            let dataExecutionError: unknown = undefined;

            try {
                setIsDataSourceCollecting(true);
                const generator = this.data(this.param, reactionDataApi);
                iteratorResult = generator.next();

                while (!iteratorResult.done && !currentExecution.abortController.signal.aborted) {
                    /**
                     * Перед вызовом возможно асинхронного кода подписываемся на использованные источники
                     */
                    this.subscribeToCurrentReactionDataSources(() => this.onSourcesChanged());

                    try {
                        if (isPromise(iteratorResult.value)) {
                            setIsDataSourceCollecting(false);
                            await iteratorResult.value;
                            setIsDataSourceCollecting(true);
                        }
                        if (currentExecution.abortController.signal.aborted) {
                            break;
                        }
                        iteratorResult = generator.next();
                    } catch (error) {
                        setIsDataSourceCollecting(true);
                        if (currentExecution.abortController.signal.aborted) {
                            break;
                        }
                        iteratorResult = generator.throw(error);
                    }
                }
            } catch (error) {
                dataExecutionError = error;
            } finally {
                setIsDataSourceCollecting(false);
                currentExecution.isDataLoading = false;
            }

            /**
             * После последнего асинхронного вызова могли быть использованы синхронные источники.
             * Так же, здесь происходит подписка если в *data не было yield
             */
            this.subscribeToCurrentReactionDataSources(() => this.onSourcesChanged());

            if (currentExecution.abortController.signal.aborted) {
                return;
            }

            /**
             * Запускаем effect только если успешно закончили data генератор
             */
            if (iteratorResult && iteratorResult.done) {
                const value = iteratorResult.value;
                if (!isSkipToken(value)) {
                    await this.effect(value, reactionEffectApi);
                    this.prevData = value;
                }
            } else {
                throw dataExecutionError;
            }
        } finally {
            currentExecution.isDataLoading = false;

            // По завершению запуска удаляем информацию о нем
            this.executions = this.executions.filter(execution => execution !== currentExecution);

            // Досчитавший экземпляр без потребителей больше не нужно отменять, а держать на него
            // ссылку в реакции нельзя: вместе с ним живут параметр запуска и его источники
            if (this.executions.length === 0) {
                this.orphanedInstances.delete(this);
            }
        }
    }

    /**
     * Callback для уведомления об изменении источников данных
     * @returns функция-обработчик уведомлений от data sources
     */
    private onSourcesChanged(): void {
        this.notificationsCount++;
        this.monitoring.onNotify(this.notificationsCount);

        // Нотификация реакции об изменении произошла в момент выполнения `data` этой или другой реакции
        if (getIsDataSourceCollecting()) {
            reportError(
                new SideEffectInDataError('Вызван триггер реакции из data реакции', {
                    cause: this.reactionError,
                    additional: {
                        instanceKey: safeJsonStringify(this.param),
                    },
                })
            );
        }

        // Реакцию не нужно запускать в трех случаях:
        // Если у экземпляра нет пользователей
        // Если уже есть отложенный вызов
        // Если подписки приостановлены
        if (this.countOfUses === 0 || this.hasDeferredRun || this.isSubscriptionsStopped) {
            return;
        }

        // Повторный запуск
        this.schedule();
    }
}
